import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  LoadingController,
  ModalController,
  ToastController,
} from "@ionic/angular";
import { lastValueFrom } from "rxjs";
import {
  CustomerOrderService,
  InvTranService,
  ScanDocumentService,
  TransferInventoryService,
} from "src/app/services/swagger";
import { PDFDocumentProxy } from "ng2-pdf-viewer";
import { Capacitor } from "@capacitor/core";
import { DOCUMENT } from "@angular/common";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

@Component({
  selector: "app-view-pdf-modal",
  templateUrl: "./view-pdf-modal.page.html",
  styleUrls: ["./view-pdf-modal.page.scss"],
})
export class ViewPdfModalPage implements OnInit, AfterViewInit, OnDestroy {
  @Input() id: string;
  @Input() type:
    | "inventory-transaction"
    | "wo-inventory-transaction"
    | "customer-order"
    | "scan-document"
    | "woSummaryReport"
    | "woTallySheetReport"
    | "transfer-inventory"
    | "returnsSummaryReport" = "customer-order";
  @Input() resource: any;
  modalTitle: string = "";
  file: string = "";

  currentPage: number = 1;
  totalPages: number = 0;
  zoom: number = 1;

  @ViewChild('pdfContainer') private pdfContainerRef: ElementRef<HTMLDivElement>;

  // The real scroll container lives inside ng2-pdf-viewer (.ng2-pdf-viewer-container),
  // not in ion-content, because pdf-viewer has a fixed viewport height in CSS.
  private pdfScrollContainer: HTMLElement;
  private isPinching = false;
  private initialDist = 0;
  private pinchStartZoom = 1;
  private pinchOriginX = 0;
  private pinchOriginY = 0;
  private currentPinchScale = 1;
  private scrollTopBeforePinch = 0;

  private readonly touchStartFn = (e: TouchEvent) => this.onTouchStart(e);
  private readonly touchMoveFn  = (e: TouchEvent) => this.onTouchMove(e);
  private readonly touchEndFn   = (e: TouchEvent) => this.onTouchEnd();

  private pdf: PDFDocumentProxy;

  constructor(
    private customerOrderService: CustomerOrderService,
    private invTranService: InvTranService,
    private scanDocumentService: ScanDocumentService,
    private transferInventoryService: TransferInventoryService,
    private modalController: ModalController,
    private loadingCtrl: LoadingController,
    private toastController: ToastController,
    @Inject(DOCUMENT) private document: Document
  ) {}

  close() {
    this.modalController.dismiss();
  }

  ngAfterViewInit() {
    const el = this.pdfContainerRef.nativeElement;
    el.addEventListener('touchstart', this.touchStartFn, { passive: false });
    el.addEventListener('touchmove',  this.touchMoveFn,  { passive: false });
    el.addEventListener('touchend',   this.touchEndFn,   { passive: false });
  }

  ngOnDestroy() {
    const el = this.pdfContainerRef?.nativeElement;
    if (el) {
      el.removeEventListener('touchstart', this.touchStartFn);
      el.removeEventListener('touchmove',  this.touchMoveFn);
      el.removeEventListener('touchend',   this.touchEndFn);
    }
  }

  private onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 2 || !this.pdfScrollContainer) return;
    event.preventDefault();
    this.isPinching = true;
    this.initialDist = this.getPinchDistance(event);
    this.pinchStartZoom = this.zoom;
    this.currentPinchScale = 1;
    const rect = this.pdfContainerRef.nativeElement.getBoundingClientRect();
    this.pinchOriginX = ((event.touches[0].clientX + event.touches[1].clientX) / 2) - rect.left;
    this.pinchOriginY = ((event.touches[0].clientY + event.touches[1].clientY) / 2) - rect.top;
    this.pdfContainerRef.nativeElement.style.transformOrigin =
      `${this.pinchOriginX}px ${this.pinchOriginY}px`;
    this.scrollTopBeforePinch = this.pdfScrollContainer.scrollTop;
  }

  private onTouchMove(event: TouchEvent) {
    if (!this.isPinching || event.touches.length !== 2) return;
    event.preventDefault();
    this.currentPinchScale = this.getPinchDistance(event) / this.initialDist;
    this.pdfContainerRef.nativeElement.style.transform = `scale(${this.currentPinchScale})`;
  }

  private onTouchEnd() {
    if (!this.isPinching) return;
    this.isPinching = false;
    const el = this.pdfContainerRef.nativeElement;
    el.style.transform = '';
    el.style.transformOrigin = '';
    const newZoom = Math.min(3.0, Math.max(0.5,
      Math.round(this.pinchStartZoom * this.currentPinchScale * 100) / 100
    ));
    const scaleRatio = newZoom / this.pinchStartZoom;
    this.zoom = newZoom;
    const targetScrollTop = Math.max(0,
      (this.scrollTopBeforePinch + this.pinchOriginY) * scaleRatio - this.pinchOriginY
    );
    setTimeout(() => {
      if (this.pdfScrollContainer) {
        this.pdfScrollContainer.scrollTop = targetScrollTop;
      }
    }, 300);
  }

  private getPinchDistance(event: TouchEvent): number {
    const dx = event.touches[0].clientX - event.touches[1].clientX;
    const dy = event.touches[0].clientY - event.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  async ngOnInit() {
    if (this.id) {
      await this.loadPrint();
    } else if (this.resource) {
      this.modalTitle = this.resource.title;
      this.file = this.resource.fileName;
      this.modalTitle = `${this.resource.fileName}`;
      const dataUri = `data:application/pdf;base64,${this.resource.data}`;
      this.file = dataUri;
    }
  }

  onLoaded(pdf: PDFDocumentProxy) {
    this.pdf = pdf;
    this.totalPages = pdf.numPages;
    this.currentPage = 1;
    this.pdfScrollContainer = this.pdfContainerRef?.nativeElement
      ?.querySelector('.ng2-pdf-viewer-container') as HTMLElement;
  }

  onPageChange(page: number) {
    this.currentPage = page;
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  zoomIn() {
    this.zoom = Math.min(3.0, Math.round((this.zoom + 0.25) * 100) / 100);
  }

  zoomOut() {
    this.zoom = Math.max(0.5, Math.round((this.zoom - 0.25) * 100) / 100);
  }

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  async download() {
    if (Capacitor.getPlatform() === "web") {
      this.downloadOnWeb();
    } else {
      await this.onClickShare(this.file);
    }
  }

  private downloadOnWeb() {
    const base64Content = this.file.split(",")[1];
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = this.document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = this.modalTitle || "document.pdf";
    anchor.style.display = "none";
    this.document.body.appendChild(anchor);
    anchor.click();
    this.document.body.removeChild(anchor);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
  }

  async loadPrint() {
    let loadingView = await this.loadingCtrl.create({ message: "loading..." });
    await loadingView.present();
    try {
      let result: any;
      if (this.type === "customer-order") {
        result = await lastValueFrom(
          this.customerOrderService.apiCustomerorderCustomerorderreportIdGet({
            id: parseFloat(this.id),
          })
        );
      } else if (this.type === "wo-inventory-transaction") {
        result = await lastValueFrom(
          this.invTranService.apiInvTranInvtranworeportIdGet({
            id: parseFloat(this.id),
          })
        );
      } else if (this.type === "inventory-transaction") {
        result = await lastValueFrom(
          this.invTranService.apiInvTranInvtranreportIdGet({
            id: parseFloat(this.id),
          })
        );
      } else if (this.type === "scan-document") {
        result = await lastValueFrom(
          this.scanDocumentService.apiScanDocumentOpenscandocumentIdsGet({
            ids: this.id,
          })
        );
      } else if (this.type === "woSummaryReport") {
        result = await lastValueFrom(
          this.invTranService.apiInvTranWosummaryreportIdGet({
            id: parseFloat(this.id),
          })
        );
      } else if (this.type === "woTallySheetReport") {
        result = await lastValueFrom(
          this.invTranService.apiInvTranWotallysheetreportIdGet({
            id: parseFloat(this.id),
          })
        );
      } else if (this.type === "transfer-inventory") {
        result = await lastValueFrom(
          this.transferInventoryService.apiTransferinventoryTransferinventoryreportIdGet({
            id: parseFloat(this.id),
          })
        );
      } else if (this.type === "returnsSummaryReport") {
        result = await lastValueFrom(
          this.invTranService.apiInvTranInvtranreturnsummaryreportIdGet({
            id: parseFloat(this.id),
          })
        );
      }
      if (result && this.type !== "scan-document") {
        this.modalTitle = `${result.fileName}.${result.fileType}`;
        const dataUri = `data:application/pdf;base64,${result.data}`;
        this.file = dataUri;
      } else if (result && result.length && this.type === "scan-document") {
        this.modalTitle = `${result[0].fileName}.${result[0].fileType}`;
        const dataUri = `data:application/pdf;base64,${result[0].data}`;
        this.file = dataUri;
      }
      await loadingView.dismiss();
    } catch (e) {
      await loadingView.dismiss();
      await this.showToast(
        `Error loading document: ${e.message || e}`,
        "danger"
      );
    }
  }

  async print() {
    if (Capacitor.getPlatform() === "web") {
      this.pdf.getData().then((u8: any) => {
        const blob = new Blob([u8.buffer], {
          type: "application/pdf",
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const iframe = this.document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = blobUrl;
        this.document.body.appendChild(iframe);
        iframe.contentWindow.print();
      });
    } else {
      this.onClickShare(this.file);
    }
  }

  async onClickShare(file: string) {
    let loading = await this.loadingCtrl.create({
      message: "Preparing to share...",
    });
    await loading.present();
    try {
      await this.share(file, this.modalTitle);
      await loading.dismiss();
    } catch (error) {
      console.error("Error during sharing:", error);
      await loading.dismiss();
      await this.showToast(
        `Error during sharing: ${error.message || error}`,
        "danger"
      );
    }
  }

  private async share(base64Data: string, title: string) {
    try {
      const base64Content = base64Data.split(",")[1];
      const sanitizedTitle = title.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${sanitizedTitle}`;

      const savedFile = await Filesystem.writeFile({
        path: filePath,
        data: base64Content,
        directory: Directory.Cache,
        recursive: true,
      });
      await Share.share({
        title: sanitizedTitle,
        url: savedFile.uri,
        dialogTitle: "Share PDF",
      });
    } catch (error) {
      await this.showToast(
        `Error during sharing: ${error.message || error}`,
        "danger"
      );
    }
  }

  private async showToast(
    message: string,
    color: "primary" | "danger",
    isHTML: boolean = false
  ) {
    const toast = await this.toastController.create({
      message,
      color,
      duration: 5000,
      buttons: [{ text: "Close", role: "cancel" }],
      cssClass: isHTML ? "toast-html" : "",
    });
    await toast.present();
  }
}
